import { memo, useCallback } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import * as Select from '@radix-ui/react-select'
import { TextInput } from '@/components/widget-components'
import { ProvinceSelector } from './ProvinceSelector'
import { US_STATES } from './regionData'

type RegionSelectorProps = {
  country: string
  region: string | undefined
  onSelectRegion: (region: string) => void
}

const UsStateSelector = ({
  region,
  onSelectRegion,
}: Omit<RegionSelectorProps, 'country'>) => {
  const { t } = useTranslation()

  const handleValueChange = useCallback(
    (value: string) => {
      onSelectRegion(value)
    },
    [onSelectRegion]
  )

  return (
    <Select.Root
      value={region}
      onValueChange={handleValueChange}
      data-testid="form-stateselector"
    >
      <Select.Trigger
        type="button"
        className="text-pebble dark:text-snow py-2 h-full w-full inline-flex items-end gap-[5px] min-w-[150px] justify-between data-[placeholder]:text-gray-400 dark:data-[placeholder]:text-gray-400 outline-none border-b-rock border-b-2"
        data-testid="form-stateselector"
      >
        <div className="relative top-[4px] flex w-full justify-between">
          <Select.Value
            placeholder={t('regionSelector.statePlaceholder')}
            data-testid="form-stateselector"
          />
        </div>
        <Select.Icon className="" data-testid="form-stateselector">
          <ChevronDownIcon />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="bg-white dark:bg-pebble overflow-hidden border border-pebble dark:border-white"
          data-testid="form-stateselector"
        >
          <Select.ScrollUpButton
            className="p-2.5"
            data-testid="form-stateselector"
          >
            <ChevronUpIcon />
          </Select.ScrollUpButton>
          <Select.Viewport data-testid="form-stateselector">
            {US_STATES.map(({ code, name }) => {
              return (
                <Select.Item
                  key={code}
                  value={code}
                  className="hover:bg-rock min-w-[150px] justify-between p-2 cursor-pointer"
                  data-testid={`state-selector-item-${code}`}
                >
                  <Select.ItemText
                    data-testid={`state-selector-item-text-${code}`}
                  >
                    {name}
                  </Select.ItemText>
                </Select.Item>
              )
            })}
          </Select.Viewport>
          <Select.ScrollDownButton
            className="p-2.5"
            data-testid="form-stateselector"
          >
            <ChevronDownIcon />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

const RegionSelectorComponent = ({
  country,
  region,
  onSelectRegion,
}: RegionSelectorProps) => {
  const { t } = useTranslation()

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onSelectRegion(e.target.value)
    },
    [onSelectRegion]
  )

  if (country === 'CA') {
    return (
      <ProvinceSelector province={region} onSelectProvince={onSelectRegion} />
    )
  }

  if (country === 'US') {
    return <UsStateSelector region={region} onSelectRegion={onSelectRegion} />
  }

  return (
    <TextInput
      placeholder={t('regionSelector.regionPlaceholder')}
      value={region}
      onChange={handleTextChange}
      data-testid="input-region-selector"
    />
  )
}

export const RegionSelector = memo(RegionSelectorComponent)
